import { Owner } from 'api'
import React, { ReactElement, useEffect, useState } from 'react'
import { OnboardingPageProps } from '../onboardingManager'
import { ShareholderForm } from './uboForms/ShareholderForm'
import { UBOIntro } from './uboIntro'
import { removeOwnerFromList, getListOfOwnersWith25PercentShareOfClient } from './uboManagerHelper'
import { ShareholderConfirmationTree } from './uboTrees/shareholderConfirmationTree'
import { ControllingShareholders } from './controllingShareholders'
import { NoControllingShareholders } from './noControllingShareholders'
import { activateOwnerSubStep } from '../activateOwnerSteps'

export enum Show {
  INTRO = 'intro',
  FORM = 'form',
  SUMMARY = 'summary',
  CONTROLLING = 'controlling',
  NO_CONTROLLING = 'no_controlling',
}

export function UBOManager({
  onNext,
  validate,
  handleFieldChange,
  activeClient,
  setCurrentPage,
  formRef,
  setServerErrors,
  setSteps,
  clientOnboarding,
  processingClientOnboarding,
}: OnboardingPageProps): ReactElement {
  const [show, setShow] = useState<Show>(activeClient.ownersAttributes?.length ? Show.SUMMARY : Show.INTRO)
  const [editOwner, setEditOwner] = useState<Owner | null>(null)
  const [parentOwner, setParentOwner] = useState<Owner | null>(null)
  const [showSidebar, setShowSidebar] = useState<boolean>(false)

  const handleDelete = (ownerId: number) => {
    if (activeClient && activeClient.ownersAttributes) {
      onNext({
        ownersValues: [...removeOwnerFromList(activeClient.ownersAttributes, ownerId)],
      })
    }
  }

  const addShareholderToClient = () => {
    setShow(Show.FORM)
    setParentOwner(null)
    setEditOwner(null)
    setShowSidebar(true)
  }

  const editShareholder = (owner: Owner) => {
    setShow(Show.FORM)
    setEditOwner(owner)
    setParentOwner(owner.parentOwner ?? null)
    setShowSidebar(true)
    setServerErrors({})
  }

  const addShareholderToOwner = (owner: Owner) => {
    setShow(Show.FORM)
    setParentOwner(owner)
    setEditOwner(null)
    setShowSidebar(true)
    setServerErrors({})
  }

  const onCancel = () => {
    if (activeClient.ownersAttributes?.length) {
      setShow(Show.SUMMARY)
    } else {
      setShow(Show.INTRO)
    }
    setParentOwner(null)
    setEditOwner(null)
    setServerErrors({})
  }

  const onSuccess = () => {
    setShow(Show.SUMMARY) // Show summary while waiting for update
    setParentOwner(null)
    setEditOwner(null)
  }

  const onContinue = () => {
    if (getListOfOwnersWith25PercentShareOfClient(activeClient).length) {
      setShow(Show.CONTROLLING)
    } else {
      setShow(Show.NO_CONTROLLING)
    }
  }

  useEffect(() => {
    activateOwnerSubStep(setSteps, 'shareholders')
  }, [setSteps])

  if (show === Show.INTRO) {
    return <UBOIntro setShow={setShow} />
  } else if (show === Show.SUMMARY) {
    return (
      <ShareholderConfirmationTree
        addShareholderToClient={addShareholderToClient}
        editShareholder={editShareholder}
        addShareholderToOwner={addShareholderToOwner}
        onContinue={onContinue}
      />
    )
  } else if (show === Show.CONTROLLING) {
    return (
      <ControllingShareholders
        onNext={onNext}
        goBack={onCancel}
        setCurrentPage={setCurrentPage}
        processingClientOnboarding={processingClientOnboarding}
        activeClient={activeClient}
        setSteps={setSteps}
        clientOnboarding={clientOnboarding}
        formRef={formRef}
        validate={validate}
        handleFieldChange={handleFieldChange}
        setServerErrors={setServerErrors}
      />
    )
  } else if (show === Show.NO_CONTROLLING) {
    return (
      <NoControllingShareholders
        onNext={onNext}
        validate={validate}
        handleFieldChange={handleFieldChange}
        activeClient={activeClient}
        setCurrentPage={setCurrentPage}
        formRef={formRef}
        setServerErrors={setServerErrors}
        setSteps={setSteps}
        clientOnboarding={clientOnboarding}
        processingClientOnboarding={processingClientOnboarding}
      />
    )
  }

  return (
    <ShareholderForm
      onNext={onNext}
      validate={validate}
      handleFieldChange={handleFieldChange}
      activeClient={activeClient}
      setCurrentPage={setCurrentPage}
      formRef={formRef}
      setServerErrors={setServerErrors}
      setSteps={setSteps}
      clientOnboarding={clientOnboarding}
      parentOwner={parentOwner}
      editOwner={editOwner}
      onSuccess={onSuccess}
      onCancel={onCancel}
      onDelete={(owner) => {
        if (owner.id) {
          handleDelete(owner.id)
          onCancel()
        }
      }}
    />
  )
}
